import * as React from 'react'
import * as formik from 'formik'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import * as api from '@owl-nest/api-client/latest'
import { t, tf } from '@owl-nest/localize'

import * as UFE from '../../../../UFE'

import { useStep, getStepIndex, getStepProgress, Step } from '../useStep'
import { Progress } from '../components/Progress'
import { Coaching } from '../components/Coaching'
import { Actions } from '../components/Actions'
import { Tip } from '../components/Tip'

import { SolidContent } from './styles'

type TrainingProps = {
  project: model.project.ProjectForOwner
}

export function Training({ project }: TrainingProps): React.ReactElement {
  const { setStepValue } = useStep(project)

  return (
    <>
      <Progress
        progress={getStepProgress(Step.TRAINING)}
        step={getStepIndex(Step.TRAINING)}
        name={t('Discover our courses')}
      />
      <Coaching project={project}>
        {t('Would you like to know more about our online courses including personalized coaching and group workshops?')}
      </Coaching>
      <formik.Formik
        initialValues={{
          'create-my-company': false,
          crowdfunding: false,
          'webmarketing-digital': false,
        }}
        onSubmit={(values) => {
          setStepValue({
            training_types: (Object.entries(values) as [api.TrainingType, boolean][]).reduce(
              (trainingType, [key, value]) => {
                if (value) {
                  trainingType.push(key)
                }
                return trainingType
              },
              [] as api.TrainingType[],
            ),
          })
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikBag) => {
          return (
            <Form onSubmit={formikBag.handleSubmit}>
              <StyledSolidContent>
                <formik.Field name="create-my-company">
                  {({ field }: formik.FieldProps<boolean>) => {
                    return (
                      <plume.CheckboxField
                        checked={field.value}
                        {...field}
                        value={field.name}
                        label={t('"Create and launch your business" course')}
                      />
                    )
                  }}
                </formik.Field>
                <formik.Field name="webmarketing-digital">
                  {({ field }: formik.FieldProps<boolean>) => {
                    return (
                      <plume.CheckboxField
                        checked={field.value}
                        {...field}
                        value={field.name}
                        label={t('"Digital webmarketing" course')}
                      />
                    )
                  }}
                </formik.Field>
                <formik.Field name="crowdfunding">
                  {({ field }: formik.FieldProps<boolean>) => {
                    return (
                      <plume.CheckboxField
                        checked={field.value}
                        {...field}
                        value={field.name}
                        label={t('"Crowdfunding" course')}
                      />
                    )
                  }}
                </formik.Field>
                <formik.Field name="professional-coaching">
                  {({ field }: formik.FieldProps<boolean>) => {
                    return (
                      <plume.CheckboxField
                        checked={field.value}
                        {...field}
                        value={field.name}
                        label={t('Professional coaching')}
                      />
                    )
                  }}
                </formik.Field>

                <Cpf>
                  <plume.styles.copy.S.regular>
                    {t(
                      'All our courses are 100% eligible for public funds for professional training (CPF, Pôle Emploi, OPCO...).',
                    )}
                  </plume.styles.copy.S.regular>
                  <img src={UFE.IMAGES.cpf} />
                </Cpf>
              </StyledSolidContent>

              <Tip>{tf("L'équipe Formation prendra directement contact avec vous.")}</Tip>
              <Actions>
                <plume.LinkAsButton kind="secondary" type="button" onClick={() => setStepValue()}>
                  {t('Skip')}
                </plume.LinkAsButton>
                <plume.Button kind="primary">{t('Next')}</plume.Button>
              </Actions>
            </Form>
          )
        }}
      </formik.Formik>
    </>
  )
}

const StyledSolidContent = styled(SolidContent)`
  @media not screen and ${plume.BREAKPOINTS.TABLET} {
    overflow: auto;
  }
`

const Cpf = styled.div`
  align-items: center;
  background: ${plume.COLORS.PRIMARY_SAND_100};
  border: 1px solid ${plume.COLORS.PRIMARY_SAND_100};
  display: flex;
  position: relative;
  padding: 16px 14px 16px 16px;
  overflow: hidden;
  margin-top: 32px;

  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  &::before {
    content: '';
    display: block;
    position: absolute;
    right: -30px;
    height: 200%;
    width: 160px;
    background: ${plume.COLORS.PRIMARY_GREY_000};
    top: -50%;
    transform: rotate(15deg);
  }

  img {
    height: 48px;
    z-index: 1;
  }

  ${plume.styles.copy.S.regular} {
    margin-right: 40px;
  }
`

const Form = styled.form`
  height: calc(100% - (132px + 4 * 22px));

  ${plume.CheckboxField} {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`
